












import Vue from 'vue';
import { ErrorUtils } from '@/utils';
import { ColourVariantType } from '@/types';
import SnotifyConfiguration from '@/classes/SnotifyConfiguration';
import {
  BackgroundExportNotificationLinkModel,
  BackgroundExportRequestDataModel,
  SearchResultsDocumentModel,
  BackgroundRequestDocumentModel,
  QueueBackgroundTaskResultModel
} from '@/services/GeneratedApi';
import ApiConfiguration from '@/classes/ApiConfiguration';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import { DocumentExportService } from '@/services/DocumentExportService';
import { AppSettingsHelper } from '@/mixins/appSettingsHelper';
import { ModalHelpers } from '@/mixins';
import { BvModalEvent } from 'bootstrap-vue';

export default Vue.extend({
  name: 'BackgroundExport',
  mixins: [AppSettingsHelper, ModalHelpers],
  data() {
    return {
      showModal: false,
      exportStatus: '',
      backgroundExportLinkData: {} as BackgroundExportNotificationLinkModel,
      exportRequest: {} as BackgroundExportRequestDataModel,
      cancelled: false
    };
  },
  props: {
    selectedDocuments: {
      type: Array as () => SearchResultsDocumentModel[]
    },
    includeRelatedDocuments: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    async exportDocuments(this: any) {
      this.exportRequest = {
        documents: this.selectedDocuments.map(({ cabinetId, documentId, documentTypeId }: SearchResultsDocumentModel) => ({
          documentId,
          cabinetId,
          documentTypeId
        })),
        includeRelatedDocuments: this.includeRelatedDocuments
      } as BackgroundExportRequestDataModel;

      const totalPageCount = this.selectedDocuments.reduce((p: number, c: SearchResultsDocumentModel) => p + c.pages!, 0);
      const backgroundExportPageThreshold = this.appSettings.backgroundExportOptions.backgroundExportPageThreshold;

      if (totalPageCount > backgroundExportPageThreshold || this.includeRelatedDocuments) {
        await this.queueExport(totalPageCount);
        return;
      }

      this.exportStatus = this.exportRequest.documents.length === 1 ? 'Exporting 1 document' : `Exporting ${this.exportRequest.documents.length} documents`;
      this.showModal = true;
      try {
        this.backgroundExportLinkData = await ApiConfiguration.api().documentExport.documentExportBackgroundexportCreate(this.exportRequest);
      } catch (error) {
        this.$log(error);
        this.showModal = false;
        this.$emit('link-action:completed');
        return;
      }

      if (this.backgroundExportLinkData.fileCount === 0) {
        SnotifyConfiguration.showInformation(this.backgroundExportLinkData.notification ?? 'Zero documents exported.');
        this.showModal = false;
        this.$emit('link-action:completed');
        return;
      }

      await this.downloadZips();
    },
    async queueExport(this: any, totalPageCount: number) {
      const confirmation = await ModalHelpers.methods.showConfirmationModalAsync(
        this.$bvModal,
        (this.includeRelatedDocuments ? 'Exporting documents with related documents will take a while.' : `Exporting ${totalPageCount} document pages will take a while.`) +
          // prettier-ignore
          ' The process will continue in the background and you\'ll receive a notification when it\'s complete. Do you want to continue?',
        {
          okVariant: ColourVariantType.Primary,
          cancelVariant: ColourVariantType.Danger,
          okTitle: 'Continue'
        }
      );

      if (!confirmation) {
        this.$emit('link-action:completed');
        return;
      }

      try {
        const result: QueueBackgroundTaskResultModel = await ApiConfiguration.api().documentExport.documentExportQueuebackgroundexportCreate(this.exportRequest);
        SnotifyConfiguration.showSuccess(result.endUserMessage ?? 'Export queued successfully.');
      } catch (error) {
        this.$log(error);
      }

      this.$emit('link-action:completed');
    },
    async downloadZips(this: any) {
      let fileNumber = 1;
      do {
        if (this.cancelled) {
          return;
        }
        await this.downloadZip(fileNumber);
        fileNumber++;
      } while (fileNumber > 0 && fileNumber <= this.backgroundExportLinkData.fileCount);

      this.showModal = false;
      if (!this.backgroundExportLinkData?.success) {
        SnotifyConfiguration.showInformation(this.backgroundExportLinkData?.notification ?? 'Error exporting documents.');
      } else {
        SnotifyConfiguration.showSuccess(this.backgroundExportLinkData?.notification ?? 'Documents exported successfully.');
      }

      try {
        await ApiConfiguration.api().documentExport.documentExportBackgroundexportzipDelete(this.backgroundExportLinkData.id);
      } catch (error) {
        this.$log(error);
      }

      this.$emit('link-action:completed');
    },
    async downloadZip(this: any, fileNumber: number) {
      this.exportStatus = `Downloading zip file ${fileNumber} of ${this.backgroundExportLinkData.fileCount}`;
      let filename = `${this.backgroundExportLinkData.zipFilename}_${fileNumber}`;

      try {
        const response = await DocumentExportService.getExportZipFileAsync(this.backgroundExportLinkData.id, fileNumber);
        const contentDisposition = response.headers['content-disposition'];

        const blobObject = new Blob([response.data]);
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename=(.+);/);
          if (fileNameMatch.length === 2) {
            filename = filename.concat('.', fileNameMatch[1].split('.').pop());
          }
        }

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blobObject, filename);
          return;
        }
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        try {
          const errorModel = await ErrorUtils.getExceptionResponseModelFromBlob(error.response.data);
          SnotifyConfiguration.showNotification(errorModel.notification, errorModel.presentationType);
        } catch (error) {
          this.$log(error);
        }
      }
    },
    onCancel(this: any, bvModalEvent: BvModalEvent) {
      bvModalEvent.preventDefault();
      this.cancelled = true;
      this.$emit('link-action:completed');
    }
  },
  components: {
    LoadingSpinner
  },

  async mounted(this: any) {
    await this.exportDocuments();
  }
});
